/* eslint-disable react-hooks/exhaustive-deps */
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import {
    addDoc,
    collection,
    deleteDoc,
    doc,
    getDoc,
    getDocs,
    onSnapshot,
    orderBy,
    query,
    serverTimestamp,
    updateDoc,
    where,
} from "firebase/firestore";
import { auth, db } from "../../../firebase";
import { Bulletin } from "./bulletin.type";
import { useLocationState } from "../../Utils";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MatiereMoyenne, EvaluationEnrichie, BulletinComplet} from "../../api/bulletin/bulletin.type"
import { Competence } from "../competence/competence.type";
import { Matiere } from "../matiere/matiere.type";
import { Timestamp } from "firebase/firestore";
import { Evaluation } from "../evaluations/evaluation.type";
import { enrichEvaluations } from "../evaluations/evaluation.api";



export const BulletinApi = createApi({
    reducerPath: "bulletinApi",
    tagTypes: ["bulletin", "bulletins"],
    baseQuery: fakeBaseQuery(),
    endpoints: (builder) => ({
        addOrEditBulletin: builder.mutation({
            async queryFn({ id, ...data }) {
                try {
                    const user = auth.currentUser;
                    if (!user) {
                        return { error: { message: "Aucun utilisateur connecté." } };
                    }
                    const userId = user?.uid;
                    if (data?.isPublished === "true") data.isPublished = true;
                    if (data?.isPublished === "false") data.isPublished = false;
                    if (!id) {
                        
                        const docRef = await addDoc(collection(db, "bulletins"), { 
                            ...data, 
                            createdAt: serverTimestamp(), 
                            createdBy: userId, 
                            updatedAt: serverTimestamp(),
                        });
                        return {
                            data: {
                                id: docRef.id,
                                message: "Bulletin enregistré !",
                                ...data,
                            },
                        };

                    } else {
                        // Mettre à jour Firestore
                        const docRef = doc(db, "bulletins", id);
                        await updateDoc(docRef, { ...data, updatedAt: serverTimestamp() });

                        return {
                            data: {
                                message:
                                    "Bulletin modifié !",
                            },
                        };
                    }
                } catch (error: any) {
                    return { error: { status: "CUSTOM", message: error.message } };
                }
            },
            invalidatesTags: ["bulletin", "bulletins"],
        }),
        getBulletins: builder.query({
            async queryFn() {
                try {
                    return new Promise((resolve) => {
                        const q = query(
                            collection(db, "bulletins"),
                            orderBy("createdAt", "desc"),
                        );
                        onSnapshot(q, (snapshot) => {
                            const bulletins = snapshot.docs.map((doc) => ({
                                id: doc.id,
                                ...doc.data(),
                            }));
                            resolve({ data: bulletins as Bulletin[] });
                        });
                        // unsubscribe();
                    });
                } catch (error) {
                    return { error };
                }
            },
            keepUnusedDataFor: 0,
            async onCacheEntryAdded(
                arg,
                { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
            ) {
                const bulletinsRef = query(
                    collection(db, "bulletins"),
                    orderBy("createdAt", "desc"),
                );
                let unsubscribe;
                try {
                    await cacheDataLoaded;

                    unsubscribe = onSnapshot(bulletinsRef, (snapshot) => {
                        const bulletins = snapshot.docs.map((doc) => ({
                            id: doc.id,
                            ...doc.data(),
                        }));
                        updateCachedData(() => bulletins as Bulletin[]);
                    });
                } catch (error) {
                    console.error("Erreur de souscription:", error);
                }

                // Nettoyer l'écouteur Firestore quand le cache est supprimé
                await cacheEntryRemoved;
                if (unsubscribe) unsubscribe();
            },
            providesTags: ["bulletins"],
        }),
        getBulletinByUser: builder.query({
            async queryFn() {
                try {
                    const user = auth.currentUser;
                    if (!user) {
                        return { error: { message: "Aucun utilisateur connecté." } };
                    }
                    const userId = user?.uid;
                    return new Promise((resolve) => {
                        const q = query(
                            collection(db, "bulletins"),
                            where("createdBy", "in", [userId, null]),
                            orderBy("createdAt", "asc"),
                        );
                        onSnapshot(q, (snapshot) => {
                            const bulletins = snapshot.docs.map((doc) => ({
                                id: doc.id,
                                ...doc.data(),
                            }));
                            resolve({ data: bulletins as Bulletin[] });
                        });
                        // unsubscribe();
                    });
                } catch (error) {
                    return { error };
                }
            },
            keepUnusedDataFor: 0,
            async onCacheEntryAdded(
                arg,
                { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
            ) {
                const user = auth.currentUser;

                const userId = user?.uid;
                const bulletinsRef = query(
                    collection(db, "bulletins"),
                    where("createdBy", "in", [userId, null]),
                    orderBy("createdAt", "asc"),
                );
                let unsubscribe;
                try {

                    await cacheDataLoaded;

                    unsubscribe = onSnapshot(bulletinsRef, (snapshot) => {
                        const bulletins = snapshot.docs.map((doc) => ({
                            id: doc.id,
                            ...doc.data(),
                        }));
                        updateCachedData(() => bulletins as Bulletin[]);
                    });
                } catch (error) {
                    console.error("Erreur de souscription:", error);
                }

                // Nettoyer l'écouteur Firestore quand le cache est supprimé
                await cacheEntryRemoved;
                if (unsubscribe) unsubscribe();
            },
            providesTags: ["bulletins"],
        }),
        getBulletinById: builder.query({
            queryFn: async (bulletinId) => {
                try {
                    const bulletinRef = doc(db, "bulletins", bulletinId);
                    const bulletinSnap = await getDoc(bulletinRef);

                    if (!bulletinSnap.exists()) {
                        return {
                            error: { status: 404, message: "Bulletin introuvable !" },
                        };
                    }

                    return { data: { id: bulletinSnap.id, ...bulletinSnap.data() } };
                } catch (error: any) {
                    return { error: { status: 500, message: error.message } };
                }
            },
            providesTags: ["bulletin"],
        }),
        // Supprimer
        deleteBulletin: builder.mutation({
            queryFn: async (uid) => {
                try {
                    const docRef = doc(db, "bulletins", uid);
                    await deleteDoc(docRef);
                    return {
                        data: { uid, message: "Bulletin supprimé !" },
                    };
                } catch (error: any) {
                    return { error: { status: "DELETE_ERROR", message: error.message } };
                }
            },
            invalidatesTags: ["bulletin", "bulletins"],
        }),
        getBulletinByStudent: builder.query<BulletinComplet[], string>({
            async queryFn(userId: string) {
                try {
                    // 1. Récupérer les bulletins de l'étudiant
                    const bulletinsQ = query(
                        collection(db, "bulletins"),
                        where("userId", "==", userId),
                        where("isPublished", "in", ["true", true]),
                        orderBy("createdAt", "desc"),
                    );
                    const bulletinsSnap = await getDocs(bulletinsQ);
                    const bulletins = bulletinsSnap.docs.map((doc) => ({
                        id: doc.id,
                        ...doc.data(),
                    })) as Bulletin[];

                    

                    // 2. Pour chaque bulletin, récupérer les évaluations de la période
                    const bulletinsComplets: BulletinComplet[] = await Promise.all(
                        bulletins.map(async (bulletin) => {
                            const dateDebut = Timestamp.fromDate(new Date(bulletin.dateDebut));
                            const dateFin = Timestamp.fromDate(new Date(bulletin.dateFin));

                            const evalsQ = query(
                                collection(db, "evaluations"),
                                where("student", "==", userId),
                                where("createdAt", ">=", dateDebut),
                                where("createdAt", "<=", dateFin),
                                orderBy("createdAt", "asc"),
                            );

                            const evalsSnap = await getDocs(evalsQ);
                            const evaluationsRaw = evalsSnap.docs.map((doc) => ({
                                id: doc.id,
                                ...doc.data(),
                            }));

                            

                            // ✅ Réutilise enrichEvaluations existant
                            const evaluations = await enrichEvaluations(evaluationsRaw) as EvaluationEnrichie[];


                            // Moyenne par matière
                            const groupesParMatiere = new Map<string, EvaluationEnrichie[]>();
                            evaluations.forEach((ev) => {
                                const matiereId = ev.matiereData?.id ?? "unknown";
                                if (!groupesParMatiere.has(matiereId)) {
                                    groupesParMatiere.set(matiereId, []);
                                }
                                groupesParMatiere.get(matiereId)!.push(ev);
                            });

                            const moyennesParMatiere: MatiereMoyenne[] = Array.from(
                                groupesParMatiere.entries()
                            ).map(([matiereId, evals]) => {
                                const somme = evals.reduce((acc, ev) => acc + (Number(ev.note) || 0), 0); // ✅ correction ici aussi
                                return {
                                    matiereId,
                                    matiereData: evals[0].matiereData,
                                    moyenne: evals.length > 0 ? somme / evals.length : 0,
                                    nbrEvaluations: evals.length,
                                };
                            });

                            // Moyenne générale
                            const sommeGenerale = evaluations.reduce((acc, ev) => acc + (Number(ev.note) || 0), 0);
                            const moyenneGenerale = evaluations.length > 0 ? sommeGenerale / evaluations.length : 0;
                            
                            return {
                                ...bulletin,
                                evaluations,
                                moyennesParMatiere,
                                moyenneGenerale,
                            };
                        })
                    );

                    return { data: bulletinsComplets };
                } catch (error) {
                    return { error: error as Error };
                }
            },
            async onCacheEntryAdded(
                userId,
                { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
            ) {
                const bulletinsRef = query(
                    collection(db, "bulletins"),
                    where("userId", "==", userId),
                    where("isPublished", "in", ["true", true]),
                    orderBy("createdAt", "desc"),
                );
                let unsubscribe;
                try {
                    await cacheDataLoaded;

                    unsubscribe = onSnapshot(bulletinsRef, async (snapshot) => {
                        const bulletins = snapshot.docs.map((doc) => ({
                            id: doc.id,
                            ...doc.data(),
                        })) as Bulletin[];
                        const bulletinsComplets: BulletinComplet[] = await Promise.all(
                            bulletins.map(async (bulletin) => {
                                const dateDebut = Timestamp.fromDate(new Date(bulletin.dateDebut));
                                const dateFin = Timestamp.fromDate(new Date(bulletin.dateFin));

                                const evalsQ = query(
                                    collection(db, "evaluations"),
                                    where("student", "==", userId),
                                    where("createdAt", ">=", dateDebut),
                                    where("createdAt", "<=", dateFin),
                                    orderBy("createdAt", "asc"),
                                );

                                const evalsSnap = await getDocs(evalsQ);
                                const evaluationsRaw = evalsSnap.docs.map((doc) => ({
                                    id: doc.id,
                                    ...doc.data(),
                                }));



                                // ✅ Réutilise enrichEvaluations existant
                                const evaluations = await enrichEvaluations(evaluationsRaw) as EvaluationEnrichie[];


                                // Moyenne par matière
                                const groupesParMatiere = new Map<string, EvaluationEnrichie[]>();
                                evaluations.forEach((ev) => {
                                    const matiereId = ev.matiereData?.id ?? "unknown";
                                    if (!groupesParMatiere.has(matiereId)) {
                                        groupesParMatiere.set(matiereId, []);
                                    }
                                    groupesParMatiere.get(matiereId)!.push(ev);
                                });

                                const moyennesParMatiere: MatiereMoyenne[] = Array.from(
                                    groupesParMatiere.entries()
                                ).map(([matiereId, evals]) => {
                                    const somme = evals.reduce((acc, ev) => acc + (Number(ev.note) || 0), 0); // ✅ correction ici aussi
                                    return {
                                        matiereId,
                                        matiereData: evals[0].matiereData,
                                        moyenne: evals.length > 0 ? somme / evals.length : 0,
                                        nbrEvaluations: evals.length,
                                    };
                                });

                                // Moyenne générale
                                const sommeGenerale = evaluations.reduce((acc, ev) => acc + (Number(ev.note) || 0), 0);
                                const moyenneGenerale = evaluations.length > 0 ? sommeGenerale / evaluations.length : 0;

                                return {
                                    ...bulletin,
                                    evaluations,
                                    moyennesParMatiere,
                                    moyenneGenerale,
                                };
                            })
                        );
                        updateCachedData(() => bulletinsComplets);
                    });
                } catch (error) {
                    console.error("Erreur de souscription:", error);
                }

                // Nettoyer l'écouteur Firestore quand le cache est supprimé
                await cacheEntryRemoved;
                if (unsubscribe) unsubscribe();
            },
            keepUnusedDataFor: 0,
            providesTags: ["bulletins"],
        }),
    }),
});

export const {
    useAddOrEditBulletinMutation,
    useGetBulletinsQuery,
    useGetBulletinByUserQuery,
    useDeleteBulletinMutation,
    useLazyGetBulletinByIdQuery,
    useGetBulletinByStudentQuery
} = BulletinApi;

export function useBulletinFromLocation(): [
    Bulletin,
    boolean,
    string,
    (id: string) => any
] {
    const itemState = useLocationState<Bulletin | undefined>(undefined);
    const [item, setItem] = useState(itemState);
    const { id } = useParams<{ id: string }>();
    const [findById, { data, isError, isLoading }] = useLazyGetBulletinByIdQuery();
    const navigate = useNavigate();

    useEffect(() => {
        // console.log("is id", id)
        if (id && id !== undefined) {
            // console.log("id", id)
            findById(id as string);
        }
    }, [id]);
    useEffect(() => {
        if (isError && !itemState) {
            navigate(-1);
        }
    }, [isError]);
    useEffect(() => {
        if (data) {
            setItem(data as Bulletin);
        }
    }, [data]);

    return [item as Bulletin & { bio: string }, isLoading, id as string, findById];
}